'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { ArrowUp, Lock, Settings2, Square } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { SystemLoadingIcon as Loader2 } from '@/components/ui/SystemLoading';
import { LiquidGlass } from '@/components/ui/liquid-glass/LiquidGlass';
import controls from '@/components/public-site/PublicSiteControls.module.css';
import { cn } from '@/lib/cn';
import styles from './StudioComposerControls.module.css';

// Tool-only opt-in. Public homepage/PreAuth imports keep the original controls.
export { ComposerFooter } from './ComposerControls';

export const StudioPrimaryAction = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function StudioPrimaryAction({ className, children, ...props }, ref) {
    return <LiquidGlass as="button" type="button" {...props} ref={node => { if (typeof ref === 'function') ref(node as HTMLButtonElement | null); else if (ref) ref.current = node as HTMLButtonElement | null; }}
      className={cn(controls.button, controls.primary, controls.compact, styles.primary, className)}>{children}</LiquidGlass>;
  },
);

export const StudioSecondaryAction = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { iconOnly?: boolean }>(
  function StudioSecondaryAction({ className, children, iconOnly = false, ...props }, ref) {
    return <LiquidGlass as="button" type="button" {...props} ref={node => { if (typeof ref === 'function') ref(node as HTMLButtonElement | null); else if (ref) ref.current = node as HTMLButtonElement | null; }}
      className={cn(controls.button, controls.secondary, iconOnly ? controls.icon : controls.compact, styles.primary, className, iconOnly && styles.icon)}>{children}</LiquidGlass>;
  },
);

export const ComposerSettingsButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function ComposerSettingsButton({ className, ...props }, ref) {
    const t = useTranslations('chat');
    return <StudioSecondaryAction ref={ref} aria-label={t('composerSettings')} {...props} className={cn(styles.control, className)}>
      <Settings2 className="h-4 w-4 shrink-0" aria-hidden /><span data-composer-settings-label>{t('composerSettings')}</span>
    </StudioSecondaryAction>;
  },
);

export const ComposerActionButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string; stopping?: boolean; loading?: boolean; locked?: boolean;
}>(function ComposerActionButton({ label, stopping, loading, locked, className, ...props }, ref) {
  const Action = stopping || locked ? StudioSecondaryAction : StudioPrimaryAction;
  return <Action ref={ref} aria-label={label} title={label} {...props} className={cn(styles.send, className)}>
    {loading ? <Loader2 size={19} className="animate-spin" aria-hidden />
      : stopping ? <Square size={15} fill="currentColor" aria-hidden />
        : locked ? <Lock size={18} aria-hidden /> : <ArrowUp size={20} aria-hidden />}
  </Action>;
});
